import { Combobox as BaseCombobox } from '@base-ui/react/combobox';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../lib/cn';

// Root — doesn't render its own element; pass-through (generic over value type).
const Combobox = BaseCombobox.Root;

// Value — render-prop host for the selected value(s); doesn't render an element.
const ComboboxValue = BaseCombobox.Value;

function ComboboxChips({ className, ...props }: React.ComponentProps<typeof BaseCombobox.Chips>) {
  return (
    <BaseCombobox.Chips
      data-slot="combobox-chips"
      className={cn(
        'border-border-strong flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border bg-white px-2 py-1.5 text-sm',
        'focus-within:border-ring focus-within:ring-ring focus-within:ring-2',
        className,
      )}
      {...props}
    />
  );
}

function ComboboxChip({ className, ...props }: React.ComponentProps<typeof BaseCombobox.Chip>) {
  return (
    <BaseCombobox.Chip
      data-slot="combobox-chip"
      className={cn(
        'bg-neutral-150 text-foreground inline-flex items-center gap-1 rounded-md py-0.5 pr-1 pl-2 text-sm',
        'data-highlighted:bg-neutral-200',
        className,
      )}
      {...props}
    />
  );
}

function ComboboxChipRemove({
  className,
  children,
  ...props
}: React.ComponentProps<typeof BaseCombobox.ChipRemove>) {
  return (
    <BaseCombobox.ChipRemove
      data-slot="combobox-chip-remove"
      className={cn(
        'text-muted-foreground hover:text-foreground inline-flex size-4 items-center justify-center rounded-sm',
        className,
      )}
      {...props}
    >
      {children ?? <X aria-hidden="true" className="size-3" />}
    </BaseCombobox.ChipRemove>
  );
}

const comboboxInputVariants = cva(
  'placeholder:text-muted-foreground min-w-24 flex-1 outline-none',
  {
    variants: {
      variant: {
        default: 'bg-transparent text-sm',
        seamless:
          'focus-visible:inset-ring-ring rounded-none border-transparent bg-neutral-50 px-4 py-2.5 text-base focus:outline-none focus-visible:inset-ring-2',
      },
      radius: {
        md: 'rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function ComboboxInput({
  className,
  variant,
  radius,
  ...props
}: React.ComponentProps<typeof BaseCombobox.Input> & VariantProps<typeof comboboxInputVariants>) {
  return (
    <BaseCombobox.Input
      data-slot="combobox-input"
      className={cn(comboboxInputVariants({ variant, radius }), className)}
      {...props}
    />
  );
}

const ComboboxPortal = BaseCombobox.Portal;

function ComboboxPositioner({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof BaseCombobox.Positioner>) {
  return (
    <BaseCombobox.Positioner
      sideOffset={sideOffset}
      className={cn('z-50 outline-none', className)}
      {...props}
    />
  );
}

const comboboxPopupVariants = cva(
  [
    'text-foreground w-(--anchor-width) overflow-hidden border bg-white shadow-lg',
    'origin-(--transform-origin) transition-[transform,scale,opacity]',
    'data-starting-style:scale-95 data-starting-style:opacity-0',
    'data-ending-style:scale-95 data-ending-style:opacity-0',
  ],
  {
    variants: {
      radius: {
        md: 'rounded-md',
        lg: 'rounded-lg',
      },
      borderTone: {
        default: 'border-border',
        subtle: 'border-border-subtle',
      },
    },
    defaultVariants: {
      radius: 'md',
      borderTone: 'default',
    },
  },
);

function ComboboxPopup({
  className,
  radius,
  borderTone,
  ...props
}: React.ComponentProps<typeof BaseCombobox.Popup> & VariantProps<typeof comboboxPopupVariants>) {
  return (
    <BaseCombobox.Popup
      data-slot="combobox-popup"
      className={cn(comboboxPopupVariants({ radius, borderTone }), className)}
      {...props}
    />
  );
}

function ComboboxList({ className, ...props }: React.ComponentProps<typeof BaseCombobox.List>) {
  return (
    <BaseCombobox.List
      data-slot="combobox-list"
      className={cn('max-h-72 overflow-y-auto p-1 outline-none', className)}
      {...props}
    />
  );
}

function ComboboxGroup({ className, ...props }: React.ComponentProps<typeof BaseCombobox.Group>) {
  return (
    <BaseCombobox.Group
      data-slot="combobox-group"
      className={cn('not-first:mt-1', className)}
      {...props}
    />
  );
}

function ComboboxGroupLabel({
  className,
  ...props
}: React.ComponentProps<typeof BaseCombobox.GroupLabel>) {
  return (
    <BaseCombobox.GroupLabel
      data-slot="combobox-group-label"
      className={cn('text-muted-foreground px-2 py-1.5 text-xs font-medium', className)}
      {...props}
    />
  );
}

// Collection — renders the filtered items of a group; doesn't render an element.
const ComboboxCollection = BaseCombobox.Collection;

const comboboxItemVariants = cva(
  [
    'flex cursor-pointer items-center rounded-md px-2 py-1.5 text-sm outline-none select-none',
    'data-highlighted:bg-muted data-selected:font-medium',
  ],
  {
    variants: {
      gap: {
        default: 'gap-2',
        wide: 'gap-3',
      },
    },
    defaultVariants: {
      gap: 'default',
    },
  },
);

function ComboboxItem({
  className,
  gap,
  ...props
}: React.ComponentProps<typeof BaseCombobox.Item> & VariantProps<typeof comboboxItemVariants>) {
  return (
    <BaseCombobox.Item
      data-slot="combobox-item"
      className={cn(comboboxItemVariants({ gap }), className)}
      {...props}
    />
  );
}

function ComboboxItemIndicator({
  className,
  children,
  ...props
}: React.ComponentProps<typeof BaseCombobox.ItemIndicator>) {
  return (
    <BaseCombobox.ItemIndicator
      data-slot="combobox-item-indicator"
      className={cn('flex shrink-0', className)}
      {...props}
    >
      {children ?? <Check aria-hidden="true" className="size-4" />}
    </BaseCombobox.ItemIndicator>
  );
}

function ComboboxEmpty({ className, ...props }: React.ComponentProps<typeof BaseCombobox.Empty>) {
  return (
    <BaseCombobox.Empty
      data-slot="combobox-empty"
      className={cn('text-muted-foreground p-2 text-sm', className)}
      {...props}
    />
  );
}

export {
  Combobox,
  ComboboxValue,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipRemove,
  ComboboxInput,
  ComboboxPortal,
  ComboboxPositioner,
  ComboboxPopup,
  ComboboxList,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxCollection,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxEmpty,
};
